import React, { useState } from 'react';
import { Box, Paper, Typography, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface MonthEntry {
  total: number;
}

interface MonthData {
  [monthYear: string]: MonthEntry;
}

interface PopriceTableData {
  [status: string]: MonthData;
}

type ThemeColors = {
  containerBg: string;
  titleText: string;
  headerBg: string;
  headerText: string;
  yearRowBg: string;
  yearRowText: string;
  subTotalBg: string;
  subTotalText: string;
  detailRowDark: string;
  detailRowLight: string;
  detailText: string;
  totalText: string;
  border: string;
  rowHover: string;
};

interface PopriceProps {
  data: {
    poprice_table: PopriceTableData;
  };
  /** Currency symbol injected from props, e.g. '€', '$', '£', '₹'. */
  symbol?: string;
  /** Where the symbol sits relative to the amount. */
  symbolPosition?: 'prefix' | 'suffix';
  /** Optional partial override of the theme palette. */
  colors?: Partial<ThemeColors>;
  /** Optional title above the table. */
  title?: string;
}

// `name` matches the status key coming from the API and must stay in English.
// `labelKey` / `tooltipKey` are i18n keys used only for display.
const STATUS_COLUMNS: Array<{ name: string; labelKey: string; tooltipKey: string }> = [
  {
    name: 'Purchased',
    labelKey: 'Purchased',
    tooltipKey: 'Tooltip_Purchased',
  },
  {
    name: 'Baseline',
    labelKey: 'Baseline',
    tooltipKey: 'Tooltip_Baseline',
  },
  {
    name: 'Cancelled',
    labelKey: 'Cancelled',
    tooltipKey: 'Tooltip_Cancelled',
  },
  {
    name: 'Added',
    labelKey: 'Added',
    tooltipKey: 'Tooltip_Added',
  },
  {
    name: 'Forecasted',
    labelKey: 'Forecasted',
    tooltipKey: 'Tooltip_Forecasted',
  },
  {
    name: 'Submitted',
    labelKey: 'Submitted',
    tooltipKey: 'Tooltip_Submitted',
  },
  {
    name: 'Validated DM',
    labelKey: 'Validated_Dm',
    tooltipKey: 'Tooltip_Validated_Dm',
  },
  // {
  //   name: 'Refused Client',
  //   labelKey: 'Refused_Client',
  //   tooltipKey: 'Tooltip_Refused_Client',
  // },
  {
    name: 'Accepted Client',
    labelKey: 'Accepted_Client',
    tooltipKey: 'Tooltip_Accepted_Client',
  },
  {
    name: 'Invoiced',
    labelKey: 'Invoiced',
    tooltipKey: 'Tooltip_Invoiced',
  },
];

// Dark navy/purple palette sampled directly from the reference screenshot.
const DEFAULT_COLORS: ThemeColors = {
  containerBg: '#2c2b64',
  titleText: '#ffffff',
  headerBg: '#2c2b64',
  headerText: '#d8d8e5',
  yearRowBg: '#ffffff',
  yearRowText: '#2c2b64',
  subTotalBg: '#565583',
  subTotalText: '#ffffff',
  detailRowDark: '#2c2b64',
  detailRowLight: '#575784',
  detailText: '#ededf5',
  totalText: '#ffffff',
  border: '#3a3a6e',
  rowHover: '#43426f',
};

const COL_WIDTHS = {
  year: '120px',
  status: '125px',
};

export const PopriceDashboard: React.FC<PopriceProps> = ({
  data,
  symbol = '€',
  symbolPosition = 'suffix',
  colors,
  title,
}) => {
  const [t] = useTranslation();
  const displayTitle = title ?? t('Orders');
  const [expandedYears, setExpandedYears] = useState<Record<string, boolean>>({});
  const tableData = data?.poprice_table || {};

  // Merge any prop overrides over the default dark theme.
  const C: ThemeColors = { ...DEFAULT_COLORS, ...colors };

  // Extract a year from a value. Returns null when nothing reliable is found,
  // so the caller can decide on a fallback (never silently guesses a year).
  const extractYear = (raw: any): string | null => {
    if (raw == null || raw === '') return null;

    const str = String(raw).trim();

    // 1. Full 4-digit year anywhere (e.g. "2025-12-31", "05-2025", "2026")
    const fourDigit = str.match(/(?:19|20)\d{2}/);
    if (fourDigit) return fourDigit[0];

    // 2. Two-digit year in MMM-YY style labels (e.g. "Dec-25", "Feb-26", "Jan-27")
    const twoDigit = str.match(/(\d{2})\s*$/);
    if (twoDigit) return `20${twoDigit[1]}`;

    return null;
  };

  // Build yearMap: year → monthYear → status → total
  const yearMap: Record<string, Record<string, Record<string, number>>> = {};

  STATUS_COLUMNS.forEach((status) => {
    const statusData = tableData[status?.name] || {};

    Object.entries(statusData).forEach(([year, itemsForYear]) => {
      Object.entries(itemsForYear || {}).forEach(([itemKey, entry]) => {
        const rawTotal = (entry as { total?: number })?.total ?? 0;
        const total = status.name === 'Cancelled' ? -Math.abs(rawTotal) : rawTotal;

        if (!yearMap[year]) yearMap[year] = {};
        if (!yearMap[year][itemKey]) yearMap[year][itemKey] = {};

        yearMap[year][itemKey][status.name] = (yearMap[year][itemKey][status.name] || 0) + total;
      });
    });
  });

  const yearTotals: Record<string, Record<string, number>> = {};
  Object.entries(yearMap).forEach(([year, itemMap]) => {
    yearTotals[year] = {};
    STATUS_COLUMNS.forEach((status) => {
      yearTotals[year][status.name] = Object.values(itemMap).reduce((sum, im) => sum + (im[status.name] || 0), 0);
    });
  });

  const grandTotals: Record<string, number> = {};
  STATUS_COLUMNS.forEach((status) => {
    grandTotals[status.name] = Object.values(yearTotals).reduce((sum, yt) => sum + (yt[status.name] || 0), 0);
  });

  // Numeric year sorting (e.g., 2024 -> 2025 -> 2026)
  const years = Object.keys(yearMap).sort((a, b) => Number(a) - Number(b));

  // Currency formatter driven entirely by props (symbol, position).
  const fmt = (val: number) => {
    if (!val) return '';
    const amount = val.toLocaleString();
    return symbolPosition === 'prefix' ? `${symbol}${amount}` : `${amount} ${symbol}`;
  };

  const toggleYear = (year: string) => setExpandedYears((prev) => ({ ...prev, [year]: !prev[year] }));

  if (!data || years.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', backgroundColor: C.containerBg }}>
        <Typography sx={{ color: C.headerText }}>No data available</Typography>
      </Box>
    );
  }

  const RowCell = ({
    children,
    align = 'flex-end',
    bold = false,
    color = C.detailText,
    first = false,
  }: {
    children?: React.ReactNode;
    align?: string;
    bold?: boolean;
    color?: string;
    first?: boolean;
  }) => (
    <Box
      sx={{
        width: first ? COL_WIDTHS.year : COL_WIDTHS.status,
        minWidth: first ? COL_WIDTHS.year : COL_WIDTHS.status,
        display: 'flex',
        justifyContent: align,
        alignItems: 'center',
        px: 1.5,
        py: 0.5,
        fontWeight: bold ? 600 : 400,
        fontSize: '14px',
        color,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      }}
    >
      {children}
    </Box>
  );

  return (
    <Box sx={{ p: 0.5, backgroundColor: C.containerBg, borderRadius: 2 }}>
      <Typography sx={{ mb: 0.5, fontWeight: 500, color: C.titleText, fontSize: '1rem' }}>{displayTitle}</Typography>

      <Paper
        elevation={2}
        sx={{
          backgroundColor: C.containerBg,
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: '70vh',
          width: '100%',
        }}
      >
        {/* Inner track sized to its content so all rows keep their column
            widths, stay aligned, and scroll together as one unit. */}
        <Box sx={{ minWidth: 'max-content' }}>
          {/* ── HEADER ROW ── */}
          <Box
            sx={{
              display: 'flex',
              backgroundColor: C.headerBg,
              borderBottom: `1px solid ${C.border}`,
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <RowCell first color={C.headerText} align="flex-start">
              {t('Years')}
            </RowCell>
            {STATUS_COLUMNS.map((col) => {
              const tooltip = t(col.tooltipKey);
              return (
                <Tooltip
                  key={col.name}
                  title={tooltip}
                  arrow
                  placement="top"
                  disableHoverListener={!tooltip}
                  componentsProps={{
                    tooltip: {
                      sx: { fontSize: 13, padding: '4px 6px', borderRadius: '6px' },
                    },
                  }}
                >
                  <Box component="span" sx={{ display: 'inline-flex' }}>
                    <RowCell color={C.headerText}>{t(col.labelKey)}</RowCell>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          {/* ── YEAR GROUPS ── */}
          {years.map((year) => {
            const isOpen = !!expandedYears[year];
            const items = Object.keys(yearMap[year]);

            return (
              <Box key={year}>
                {/* Year summary row (light) */}
                <Box
                  onClick={() => toggleYear(year)}
                  sx={{
                    display: 'flex',
                    backgroundColor: C.yearRowBg,
                    borderBottom: `1px solid ${C.border}`,
                    cursor: 'pointer',
                    '&:hover': { filter: 'brightness(0.97)' },
                  }}
                >
                  <RowCell first bold color={C.yearRowText} align="flex-start">
                    <Box component="span" sx={{ mr: 0.75, fontSize: '0.8rem', color: 'gray' }}>
                      {isOpen ? '⊟' : '⊞'}
                    </Box>
                    {year}
                  </RowCell>
                  {STATUS_COLUMNS.map((col) => (
                    <RowCell key={col.name} bold color={C.yearRowText}>
                      {fmt(yearTotals[year][col.name])}
                    </RowCell>
                  ))}
                </Box>

                {/* Sub-total row (purple) */}
                <Box
                  sx={{
                    display: 'flex',
                    backgroundColor: C.subTotalBg,
                    borderBottom: `1px solid ${C.border}`,
                  }}
                >
                  <RowCell first color={C.subTotalText} align="flex-start" />
                  {STATUS_COLUMNS.map((col) => (
                    <RowCell key={col.name} color={C.subTotalText}>
                      {fmt(yearTotals[year][col.name])}
                    </RowCell>
                  ))}
                </Box>

                {/* Month detail rows (alternating dark / medium purple) */}
                {isOpen &&
                  items.map((itemKey, idx) => (
                    <Box
                      key={itemKey}
                      sx={{
                        display: 'flex',
                        backgroundColor: idx % 2 === 0 ? C.detailRowDark : C.detailRowLight,
                        borderBottom: `1px solid ${C.border}`,
                        '&:hover': { backgroundColor: C.rowHover },
                      }}
                    >
                      <RowCell first color={C.detailText} align="flex-start">
                        <Box component="span" sx={{ pl: 2 }}>
                          {itemKey}
                        </Box>
                      </RowCell>
                      {STATUS_COLUMNS.map((col) => (
                        <RowCell key={col.name} color={C.detailText}>
                          {fmt(yearMap[year][itemKey][col.name] || 0)}
                        </RowCell>
                      ))}
                    </Box>
                  ))}
              </Box>
            );
          })}

          {/* ── TOTAL ROW ── */}
          <Box
            sx={{
              display: 'flex',
              backgroundColor: C.yearRowBg,
            }}
          >
            <RowCell first bold color={C.yearRowText} align="flex-start">
              {t('Total')}
            </RowCell>
            {STATUS_COLUMNS.map((col) => (
              <RowCell key={col.name} bold color={C.yearRowText}>
                {fmt(grandTotals[col.name])}
              </RowCell>
            ))}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default PopriceDashboard;
